import {
  colorsToCssVars,
  darkCategoryColors,
  darkColors,
  lightCategoryColors,
  lightColors,
  motion,
  radius,
  shadow,
  space,
  typography,
} from '@financas/ui-tokens';

/**
 * Injeta os tokens como custom properties. Web e mobile leem o mesmo pacote,
 * entao a identidade nao se divide em duas fontes que divergem com o tempo.
 */

export type ThemeName = 'dark' | 'light';

const STORAGE_KEY = 'financas.theme';

function staticVars(): string {
  const lines: string[] = [];

  lines.push(`  --font-sans: ${typography.fontSans};`);
  lines.push(`  --font-serif: ${typography.fontSerif};`);
  lines.push(`  --font-mono: ${typography.fontMono};`);

  for (const [key, value] of Object.entries(typography.size)) {
    lines.push(`  --text-${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(space)) {
    lines.push(`  --space-${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(radius)) {
    lines.push(`  --radius-${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(shadow)) {
    lines.push(`  --shadow-${key}: ${value};`);
  }

  lines.push(`  --motion-fast: ${motion.fast};`);
  lines.push(`  --motion-base: ${motion.base};`);
  lines.push(`  --motion-easing: ${motion.easing};`);

  return lines.join('\n');
}

export function applyTheme(theme: ThemeName): void {
  const colors = theme === 'dark' ? darkColors : lightColors;
  const categorias = theme === 'dark' ? darkCategoryColors : lightCategoryColors;
  const css = `:root {\n${colorsToCssVars(colors)}\n${categoryVars(categorias)}\n${staticVars()}\n}`;

  let style = document.getElementById('financas-tokens');
  if (!style) {
    style = document.createElement('style');
    style.id = 'financas-tokens';
    document.head.appendChild(style);
  }
  style.textContent = css;

  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
}

/**
 * As cores das categorias como variaveis CSS.
 *
 * Duas por token: `--cat-rose` para o ponto e a fatia do grafico,
 * `--cat-rose-soft` para o fundo da etiqueta. Escritas junto com o resto do
 * tema porque mudam com ele: um fundo suave calculado para o escuro fica
 * invisivel no claro.
 */
function categoryVars(cores: Record<string, { solid: string; soft: string }>): string {
  return Object.entries(cores)
    .map(([token, cor]) => `  --cat-${token}: ${cor.solid};\n  --cat-${token}-soft: ${cor.soft};`)
    .join('\n');
}

export function loadTheme(): ThemeName {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  // Escuro e o padrao da especificacao, mesmo que o sistema prefira claro.
  return 'dark';
}

export function saveTheme(theme: ThemeName): void {
  localStorage.setItem(STORAGE_KEY, theme);
  applyTheme(theme);
}
